// @greenlight-spec: 20-agent-plugins.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { Disclosure } from '@/components/greenlight/Disclosure';
import { SectionCard } from '@/components/greenlight/SectionCard';
import {
  buildCopilotManagedSettings,
  buildCopilotMcpAllowlistMatcher,
  buildCopilotPermissions,
  formatCopilotSettingsJson,
} from '@/lib/copilotManagedSettings';

import { AgentMark, COPILOT_MARK } from './AgentMark';
import { CodeBlock } from './CodeBlock';
import { CopyRow } from './CopyRow';

function Tag({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </span>
  );
}

export interface CopilotManagedSettingsCardsProps {
  marketplaceRepo: string;
  mcpUrl: string;
}

/** Copilot org-managed plugin, MCP allowlist and approval snippets shared by Settings and the install wizard. */
export function CopilotManagedSettingsCards({
  marketplaceRepo,
  mcpUrl,
}: CopilotManagedSettingsCardsProps): React.ReactElement {
  const managedJson = formatCopilotSettingsJson(buildCopilotManagedSettings(marketplaceRepo));
  const allowlistJson = formatCopilotSettingsJson(buildCopilotMcpAllowlistMatcher(mcpUrl));
  const permissionsJson = formatCopilotSettingsJson(buildCopilotPermissions(mcpUrl));

  return (
    <div className="flex flex-col gap-4" data-testid="copilot-managed-settings-cards">
      <SectionCard
        title="Copilot — organization managed settings"
        icon={<AgentMark {...COPILOT_MARK} />}
        headerBar
        badge={<Tag>IT rollout</Tag>}
      >
        <div className="flex flex-col gap-3 p-5">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground marker:text-foreground">
            <li>Enable MCP servers in Copilot for your GitHub organization or enterprise.</li>
            <li>
              Merge this JSON into{' '}
              <span className="font-mono text-[13px] text-foreground">
                copilot/managed-settings.json
              </span>
              , usually in the enterprise{' '}
              <span className="font-mono text-[13px] text-foreground">.github-private</span>{' '}
              repository. It installs Greenlight for everyone.
            </li>
          </ol>
          <CodeBlock caption="COPILOT MANAGED SETTINGS" code={managedJson} />
          <Disclosure
            label="If you already restrict MCP servers"
            hint="allowedMcpServers"
            testId="copilot-mcp-allowlist"
          >
            <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
              <p>
                Add this matcher to your existing{' '}
                <span className="font-mono text-[13px] text-foreground">allowedMcpServers</span>{' '}
                array; don&rsquo;t replace the list with only this URL. Without it Copilot cannot
                reach <span className="font-mono">/mcp</span>, and it fails silently: the agent
                answers from its own knowledge. Registry only is a custom-registry policy, not a way
                to permit this URL.
              </p>
              <CodeBlock caption="MCP ALLOWLIST MATCHER" code={allowlistJson} />
            </div>
          </Disclosure>
        </div>
        <div className="divide-y divide-border">
          <CopyRow label="Org MCP URL" value={mcpUrl} />
        </div>
      </SectionCard>
      <SectionCard
        title="Copilot — let Greenlight run without approval prompts"
        icon={<AgentMark {...COPILOT_MARK} />}
        headerBar
        badge={<Tag>IT rollout</Tag>}
      >
        <div className="flex flex-col gap-3 p-5 text-sm text-muted-foreground">
          <p>
            Merge these rules into the same{' '}
            <span className="font-mono">copilot/managed-settings.json</span>. The{' '}
            <span className="font-mono">greenlight</span> command and this install&rsquo;s host then
            run without a prompt; everything else still asks.
          </p>
          <CodeBlock caption="COPILOT MANAGED SETTINGS · PERMISSIONS" code={permissionsJson} />
          <Disclosure
            label="Optional: no prompts at all"
            hint="Allow all, chosen by each member"
            testId="copilot-allow-all"
          >
            <div className="flex flex-col gap-3 px-4 pb-4 pt-1 text-sm text-muted-foreground">
              <p>
                Copilot&rsquo;s <span className="font-medium text-foreground">Allow all</span> is
                set by each member in their client; managed settings cannot turn it on for everyone.
                Leave <span className="font-mono">disableBypassPermissionsMode</span> unset so they
                can.
              </p>
              <ul className="flex list-disc flex-col gap-2 pl-5">
                <li>
                  Copilot CLI: start with <span className="font-mono">copilot --allow-all</span>, or
                  run <span className="font-mono">/allow-all</span> in a session.
                </li>
                <li>
                  VS Code: turn on <span className="font-mono">chat.tools.global.autoApprove</span>.
                </li>
                <li>
                  Copilot app: set{' '}
                  <span className="font-medium text-foreground">Tool Permissions</span> to{' '}
                  <span className="font-medium text-foreground">Allow all</span> in the session
                  settings.
                </li>
              </ul>
            </div>
          </Disclosure>
        </div>
      </SectionCard>
    </div>
  );
}
